// src/filters/index.js
import Vue from 'vue'
import { formatRupiahAmount } from '@/utils/money'
import i18n from '@/i18n'

const parseRentalDate = value => {
  if (value === null || value === undefined) return null
  const rawValue = String(value).trim()
  if (!rawValue || rawValue === '0') return null

  let timestamp
  if (/^-?\d+$/.test(rawValue)) {
    const numericValue = Number(rawValue)
    if (!Number.isFinite(numericValue) || numericValue <= 0) return null
    timestamp = numericValue < 100000000000 ? numericValue * 1000 : numericValue
  } else {
    timestamp = Date.parse(rawValue)
  }

  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime()) || date.getFullYear() < 2001) return null
  return date
}

const rentalDateLocale = () => {
  const locale = String(i18n.locale || 'en-US')
  return locale.toLowerCase().startsWith('id') ? 'id-ID' : locale
}

const monthShortEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const monthShortId = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']
const pad = n => n.toString().padStart(2, '0')
const isIndonesianLocale = locale => String(locale || '').toLowerCase().startsWith('id')

export const formatRentalDate = (value, fallback = '-') => {
  const date = parseRentalDate(value)
  if (!date) return fallback
  const locale = rentalDateLocale()
  if (isIndonesianLocale(locale)) {
    return `${date.getDate()} ${monthShortId[date.getMonth()]} ${date.getFullYear()}`
  }
  return `${monthShortEn[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}

export const formatRentalDateTime = (value, fallback = '-') => {
  const date = parseRentalDate(value)
  if (!date) return fallback

  const locale = rentalDateLocale()
  const dateText = formatRentalDate(value, fallback)
  if (isIndonesianLocale(locale)) {
    return `${dateText} ${pad(date.getHours())}:${pad(date.getMinutes())} WIB`
  }
  const hour12 = date.getHours() % 12 || 12
  const meridiem = date.getHours() < 12 ? 'AM' : 'PM'
  return `${dateText} at ${pad(hour12)}:${pad(date.getMinutes())} ${meridiem}`
}

export const formatPaymentResultDateTime = (value, fallback = '-') => {
  const date = parseRentalDate(value)
  if (!date) return fallback
  const locale = rentalDateLocale()
  if (isIndonesianLocale(locale)) {
    return `${formatRentalDate(value, fallback)} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())} WIB`
  }
  const hour12 = date.getHours() % 12 || 12
  const meridiem = date.getHours() < 12 ? 'AM' : 'PM'
  return `${monthShortEn[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} ${pad(hour12)}:${pad(date.getMinutes())} ${meridiem}`
}

Vue.filter('rentalDateFormat', formatRentalDate)
Vue.filter('rentalDateTimeFormat', formatRentalDateTime)
Vue.filter('paymentResultDateTimeFormat', formatPaymentResultDateTime)

/**
 * 日期格式化
 * @param {Date|string|number} date - 日期
 * @param {string} format - 格式，默认 'dd/MM/yyyy HH:mm:ss WIB'
 */
Vue.filter('dateFormat', function (date, format = 'dd/MM/yyyy HH:mm:ss WIB') {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''

  const year = d.getFullYear()
  const month = d.getMonth() + 1
  const day = d.getDate()
  const hour = d.getHours()
  const minute = d.getMinutes()
  const second = d.getSeconds()
  const pad = n => n.toString().padStart(2, '0')

  const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const map = {
    'YYYY': year,
    'yyyy': year,
    'YY': String(year).slice(-2),
    'yy': String(year).slice(-2),
    'Mon': monthShort[month - 1],
    'MM': pad(month),
    'M': month,
    'DD': pad(day),
    'dd': pad(day),
    'D': day,
    'HH': pad(hour),
    'H': hour,
    'hh': pad(hour % 12 || 12),
    'h': hour % 12 || 12,
    'mm': pad(minute),
    'm': minute,
    'ss': pad(second),
    's': second,
    'A': hour < 12 ? 'AM' : 'PM',
    'a': hour < 12 ? 'am' : 'pm'
  }

  return format.replace(/YYYY|yyyy|YY|yy|Mon|MM|M|DD|dd|D|HH|H|hh|h|mm|m|ss|s|A|a/g,
    match => map[match] || match)
})

/**
 * 数字格式化（千分位）
 * @param {number} num - 数字
 * @param {number} decimals - 小数位数
 */
Vue.filter('numberFormat', function (num, decimals = 2) {
  if (num === null || num === undefined) return ''

  return formatRupiahAmount(num, decimals)
})

Vue.filter('ordinalSuffix', function (num) {
  num = Number(num);
  if (num >= 11 && num <= 13) return num + 'th';
  switch (num % 10) {
    case 1: return num + 'st';
    case 2: return num + 'nd';
    case 3: return num + 'rd';
    default: return num + 'th';
  }
})

/**
 * 货币格式化
 * @param {number} num - 金额
 * @param {string} symbol - 货币符号，默认 '¥'
 * @param {number} decimals - 小数位数
 */
Vue.filter('currency', function (num, symbol = '¥', decimals = 2) {
  if (num === null || num === undefined) return ''

  const n = Number(num)
  if (isNaN(n)) return ''

  const fixed = n.toFixed(decimals)
  const parts = fixed.split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return symbol + parts.join('.')
})

/**
 * 百分比格式化
 * @param {number} num - 小数
 * @param {number} decimals - 小数位数
 */
Vue.filter('percent', function (num, decimals = 2) {
  if (num === null || num === undefined) return ''

  const n = Number(num)
  if (isNaN(n)) return ''

  return (n * 100).toFixed(decimals) + '%'
})

/**
 * 文本截断
 * @param {string} text - 文本
 * @param {number} length - 保留长度
 * @param {string} suffix - 后缀，默认 '...'
 */
Vue.filter('truncate', function (text, length = 20, suffix = '...') {
  if (!text) return ''
  if (text.length <= length) return text

  return text.substring(0, length) + suffix
})

/**
 * 手机号脱敏
 * @param {string} phone - 手机号
 */
Vue.filter('phoneMask', function (phone) {
  if (!phone) return ''
  const str = String(phone)
  return str.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
})

/**
 * 身份证脱敏
 * @param {string} idCard - 身份证号
 */
Vue.filter('idCardMask', function (idCard) {
  if (!idCard) return ''
  const str = String(idCard)
  if (str.length === 18) {
    return str.replace(/(\d{6})\d{8}(\w{4})/, '$1********$2')
  }
  return str
})

/**
 * 文件大小格式化
 * @param {number} bytes - 字节数
 */
Vue.filter('fileSize', function (bytes) {
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
})

/**
 * 时间间隔格式化
 * @param {Date|string|number} date - 日期
 */
Vue.filter('timeAgo', function (date) {
  if (!date) return ''

  const d = new Date(date)
  if (isNaN(d.getTime())) return ''

  const now = new Date()
  const diff = now - d
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const months = Math.floor(days / 30)
  const years = Math.floor(months / 12)

  if (years > 0) return `${years}年前`
  if (months > 0) return `${months}个月前`
  if (days > 0) return `${days}天前`
  if (hours > 0) return `${hours}小时前`
  if (minutes > 0) return `${minutes}分钟前`
  if (seconds > 30) return `${seconds}秒前`
  return '刚刚'
})

/**
 * 布尔值转文字
 * @param {boolean} value - 布尔值
 * @param {string} trueText - true时显示的文字
 * @param {string} falseText - false时显示的文字
 */
Vue.filter('boolToText', function (value, trueText = '是', falseText = '否') {
  if (value === null || value === undefined) return ''
  return value ? trueText : falseText
})

/**
 * 空值处理
 * @param {any} value - 值
 * @param {string} placeholder - 占位符，默认 '-'
 */
Vue.filter('empty', function (value, placeholder = '-') {
  if (value === null || value === undefined || value === '') {
    return placeholder
  }
  return value
})

// 导出所有过滤器（可选）
export default {
  install() {
    // 已通过 Vue.filter 注册，无需额外操作
  }
}
